import { RootState } from 'App'
import {
  BusinessGoal,
  BusinessGoalsFilterData,
  CalendarEventViewPreference,
  MetricGranularity,
  UserTaskItemStatus,
  UserTaskQuickFilter,
  useGetBusinessGoalsQuery,
  useUpdateMyProfileMutation,
} from '__generated__/api-types-and-hooks'
import { useGetBusinessMetrics } from 'appHooks/useGetBusinessMetrics'
import { useGetUserTasks } from 'appHooks/useGetUserTasks'
import { GoalStats } from 'components/Common/AppGoalStatsCard/AppGoalStatsCard.component'
import { trendDirType } from 'components/Common/AppStatsCard/AppStatsCard.component'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getTenantId } from 'utils/getTenantId'

interface IUseGetUserBusinessGoalsInput {
  filter?: BusinessGoalsFilterData
}

export const useUserDashboard = (input?: IUseGetUserBusinessGoalsInput) => {
  const tenantId = getTenantId()
  const clientId = useSelector((state: RootState) => state.clients.client.profile.id)
  const currentUser = useSelector((state: RootState) => state.user.user)
  const [userBusinessGoals, setUserBusinessGoals] = useState<BusinessGoal[]>([])
  const completedTouchdowns = useRef(0)
  const [showInternalMeetingsOnly, setShowInternalMeetingsOnly] = useState(
    currentUser?.calendarEventViewPreference === CalendarEventViewPreference.Internal
  )

  const { mutate: updateUserProfile } = useUpdateMyProfileMutation()
  const {
    data,
    isLoading: isUserBusinessGoalsLoading,
    isSuccess,
    refetch: refetchUserBusinessGoals,
  } = useGetBusinessGoalsQuery(
    {
      input: { tenantId, userId: clientId || currentUser?.id, filter: input?.filter },
    },
    { refetchOnWindowFocus: false }
  )

  useEffect(() => {
    if (data && data.getBusinessGoals) {
      setUserBusinessGoals(data.getBusinessGoals as unknown as BusinessGoal[])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  const completedUserBusinessGoals = useMemo(
    () => userBusinessGoals.filter((goal) => goal.status === UserTaskItemStatus.Done),
    [userBusinessGoals]
  )

  useEffect(() => {
    let touchdowns = 0
    userBusinessGoals.forEach((goal) => {
      goal.plan?.milestones?.forEach((milestone) => {
        if (milestone.status === UserTaskItemStatus.Done) {
          touchdowns++
        }
      })
    })
    completedTouchdowns.current = touchdowns
  }, [userBusinessGoals])

  const { completedTasks } = useGetUserTasks({
    quickFilter: UserTaskQuickFilter.AllTasks,
  })

  const goalStats: GoalStats[] = [
    {
      title: 'Plays Completed',
      subtitle: 'Every play brings you closer to your goal.',
      value: completedTasks.length ?? 0,
    },
    {
      title: 'Touchdowns Completed',
      subtitle: "You're Building Something Great!",
      value: completedTouchdowns.current ?? 0,
    },
  ]

  const handleToggle = () => {
    const newPreference = !showInternalMeetingsOnly
    setShowInternalMeetingsOnly(newPreference)

    updateUserProfile({
      input: {
        tenantId: currentUser?.tenantId,
        calendarEventViewPreference: newPreference
          ? CalendarEventViewPreference.Internal
          : CalendarEventViewPreference.All,
      },
    })
  }

  // for stats section
  const [totalRevenuePeriod, setTotalRevenuePeriod] = useState<'This Month' | 'YTD'>('YTD')
  const [netProfitPeriod, setNetProfitPeriod] = useState<'This Month' | 'YTD'>('YTD')
  const [numberOfLeadsPeriod, setNumberOfLeadsPeriod] = useState<'This Month' | 'YTD'>('YTD')
  const [leadConversionRatePeriod, setLeadConversionRatePeriod] = useState<'This Month' | 'YTD'>(
    'YTD'
  )

  const revenueGranularity: MetricGranularity = useMemo(() => {
    if (totalRevenuePeriod === 'This Month') {
      return MetricGranularity.Month
    } else {
      return MetricGranularity.Year
    }
  }, [totalRevenuePeriod])

  const netProfitGranularity: MetricGranularity = useMemo(() => {
    if (netProfitPeriod === 'This Month') {
      return MetricGranularity.Month
    } else {
      return MetricGranularity.Year
    }
  }, [netProfitPeriod])

  const numberOfLeadsGranularity: MetricGranularity = useMemo(() => {
    if (numberOfLeadsPeriod === 'This Month') {
      return MetricGranularity.Month
    } else {
      return MetricGranularity.Year
    }
  }, [numberOfLeadsPeriod])

  const leadsConversionRateGranularity: MetricGranularity = useMemo(() => {
    if (leadConversionRatePeriod === 'This Month') {
      return MetricGranularity.Month
    } else {
      return MetricGranularity.Year
    }
  }, [leadConversionRatePeriod])

  //  fetch business metrics
  const {
    isRevenueLoading,
    businessRevenueMetric,
    isNetProfitLoading,
    businessNetProfitMetric,
    isNumberOfLeadsLoading,
    businessNumberOfLeadsMetric,
    isLeadsConversionRateLoading,
    businessLeadsConversionRateMetric,
    isMetricsViewEnabled,
    handleMetricsShare,
    revenueMetricError,
    netProfitError,
    numberOfLeadsError,
    leadsConversionRateError,
    refetchRevenueMetrics,
    refetchNetProfitMetrics,
    refetchNumberOfLeadsMetrics,
    refetchLeadsConversionRateMetrics,
    refetchAllMetrics,
  } = useGetBusinessMetrics({
    revenueGranularity,
    netProfitGranularity,
    numberOfLeadsGranularity,
    leadsConversionRateGranularity,
  })

  // React Query automatically refetches when the granularity parameters change,
  // so no manual useEffect refetches are needed. This prevents race conditions
  // and duplicate API calls that were causing the toggle button issue.

  // Add loading state optimization
  const isAnyMetricLoading = useMemo(() => {
    return (
      isRevenueLoading ||
      isNetProfitLoading ||
      isNumberOfLeadsLoading ||
      isLeadsConversionRateLoading
    )
  }, [isRevenueLoading, isNetProfitLoading, isNumberOfLeadsLoading, isLeadsConversionRateLoading])

  const statsData = useMemo(() => {
    const revenueThisMonth: number | null =
      businessRevenueMetric?.getBusinessProfile?.revenueThisMonth?.counter?.value ?? null
    const revenueGrowthRate: number | null =
      businessRevenueMetric?.getBusinessProfile?.revenueGrowthRate?.counter?.value ?? null
    const revenueYearToDate: number | null =
      businessRevenueMetric?.getBusinessProfile?.revenueYearToDate?.counter?.value ?? null

    const netProfitThisMonth: number | null =
      businessNetProfitMetric?.getBusinessProfile?.netProfitThisMonth?.counter?.value ?? null
    const netProfitGrowthRate: number | null =
      businessNetProfitMetric?.getBusinessProfile?.netProfitGrowthRate?.counter?.value ?? null
    const netProfitYearToDate: number | null =
      businessNetProfitMetric?.getBusinessProfile?.netProfitYearToDate?.counter?.value ?? null

    const leadCount: number | null =
      businessNumberOfLeadsMetric?.getBusinessProfile?.leadCount?.counter?.value ?? null
    const leadCountGrowthRate: number | null =
      businessNumberOfLeadsMetric?.getBusinessProfile?.leadCountGrowthRate?.counter?.value ?? null

    const leadConversionRate: number | null =
      businessLeadsConversionRateMetric?.getBusinessProfile?.leadConversionRate?.counter?.value ??
      null
    const leadConversionRateGrowthRate: number | null =
      businessLeadsConversionRateMetric?.getBusinessProfile?.leadConversionRateGrowthRate?.counter
        ?.value ?? null

    return {
      totalRevenue: {
        title: 'Total Revenue',
        value: revenueThisMonth !== null ? `$${revenueThisMonth?.toFixed(2)}` : '---',
        changeValue: revenueGrowthRate !== null ? `${revenueGrowthRate?.toFixed(2)}%` : '---',
        changeTrend: (revenueGrowthRate !== null && revenueGrowthRate > 0
          ? 'up'
          : revenueGrowthRate !== null && revenueGrowthRate < 0
          ? 'down'
          : 'neutral') as trendDirType,
        subHeading: 'Finance',
        subValue: {
          prefix:
            revenueYearToDate !== null && revenueYearToDate > 0
              ? '+'
              : revenueYearToDate !== null && revenueYearToDate < 0
              ? '-'
              : '',
          value:
            revenueYearToDate !== null
              ? revenueYearToDate !== 0
                ? `$${Math.abs(revenueYearToDate)?.toFixed(2)}`
                : 'No Change'
              : 'No change',
          suffix: totalRevenuePeriod === 'YTD' ? 'vs. prior year' : 'vs. last month',
          trend: (revenueYearToDate !== null && revenueYearToDate > 0
            ? 'up'
            : revenueYearToDate !== null && revenueYearToDate < 0
            ? 'down'
            : 'neutral') as trendDirType,
        },
      },
      netProfit: {
        title: 'Net Profit',
        value: netProfitThisMonth !== null ? `$${netProfitThisMonth?.toFixed(2)}` : '---',
        changeValue: netProfitGrowthRate !== null ? `${netProfitGrowthRate?.toFixed(2)}%` : '---',
        changeTrend: (netProfitGrowthRate !== null && netProfitGrowthRate > 0
          ? 'up'
          : netProfitGrowthRate !== null && netProfitGrowthRate < 0
          ? 'down'
          : 'neutral') as trendDirType,
        subHeading: 'Finance',
        subValue: {
          prefix:
            netProfitYearToDate !== null && netProfitYearToDate > 0
              ? '+'
              : netProfitYearToDate !== null && netProfitYearToDate < 0
              ? '-'
              : '',
          value:
            netProfitYearToDate !== null
              ? netProfitYearToDate !== 0
                ? `$${Math.abs(netProfitYearToDate)?.toFixed(2)}`
                : 'No Change'
              : 'No change',
          suffix: netProfitPeriod === 'YTD' ? 'vs. prior year' : 'vs. last month',
          trend: (netProfitYearToDate !== null && netProfitYearToDate > 0
            ? 'up'
            : netProfitYearToDate !== null && netProfitYearToDate < 0
            ? 'down'
            : 'neutral') as trendDirType,
        },
      },
      numberOfLeads: {
        title: 'Number of Leads',
        titleTooltip:
          'Total number of potential clients/customers that show interest in your business through online or offline actions (eg. signups, walk-ins, ads etc) and have the potential to convert into paying customers',
        value: leadCount !== null ? `${leadCount}` : '---',
        changeValue: leadCountGrowthRate !== null ? `${leadCountGrowthRate.toFixed(2)}%` : '---',
        changeTrend: (leadCountGrowthRate !== null && leadCountGrowthRate > 0
          ? 'up'
          : leadCountGrowthRate !== null && leadCountGrowthRate < 0
          ? 'down'
          : 'neutral') as trendDirType,
        subHeading: 'Sales',
        subValue: {
          prefix:
            leadCountGrowthRate !== null && leadCountGrowthRate > 0
              ? '+'
              : leadCountGrowthRate !== null && leadCountGrowthRate < 0
              ? '-'
              : '',
          value:
            leadCountGrowthRate !== null
              ? leadCountGrowthRate !== 0
                ? `$${Math.abs(leadCountGrowthRate)?.toFixed(2)}`
                : 'No Change'
              : 'No change',
          suffix: numberOfLeadsPeriod === 'YTD' ? 'vs. prior year' : 'vs. last month',
          trend: (leadCountGrowthRate !== null && leadCountGrowthRate > 0
            ? 'up'
            : leadCountGrowthRate !== null && leadCountGrowthRate < 0
            ? 'down'
            : 'neutral') as trendDirType,
        },
      },
      leadConversionRate: {
        title: 'Lead Conversion Rate',
        titleTooltip:
          'The percentage of potential clients/customers that showed interest in your business through online  or offline actions (eg. signups, walk-ins, ads etc) that converted into paying customers',
        value: leadConversionRate !== null ? `${leadConversionRate?.toFixed(2)}%` : '---',
        changeValue:
          leadConversionRateGrowthRate !== null
            ? `${leadConversionRateGrowthRate.toFixed(2)}%`
            : 'No change',
        changeTrend: (leadConversionRateGrowthRate !== null && leadConversionRateGrowthRate > 0
          ? 'up'
          : leadConversionRateGrowthRate !== null && leadConversionRateGrowthRate < 0
          ? 'down'
          : 'neutral') as trendDirType,
        subHeading: 'Sales',
        subValue: {
          prefix:
            leadConversionRateGrowthRate !== null && leadConversionRateGrowthRate > 0
              ? '+'
              : leadConversionRateGrowthRate !== null && leadConversionRateGrowthRate < 0
              ? '-'
              : '',
          value:
            leadConversionRateGrowthRate !== null
              ? leadConversionRateGrowthRate !== 0
                ? `${leadConversionRateGrowthRate?.toFixed(2)}%`
                : 'No Change'
              : 'No change',
          suffix: leadConversionRatePeriod === 'YTD' ? 'vs. prior year' : 'vs. last month',
          trend: (leadConversionRateGrowthRate !== null && leadConversionRateGrowthRate > 0
            ? 'up'
            : leadConversionRateGrowthRate !== null && leadConversionRateGrowthRate < 0
            ? 'down'
            : 'neutral') as trendDirType,
        },
      },
    }
  }, [
    businessRevenueMetric,
    businessNetProfitMetric,
    businessNumberOfLeadsMetric,
    businessLeadsConversionRateMetric,
    totalRevenuePeriod,
    netProfitPeriod,
    numberOfLeadsPeriod,
    leadConversionRatePeriod,
  ])

  // Add early loading state to prevent flash of content
  const isInitialLoading = useMemo(() => {
    return (
      isAnyMetricLoading &&
      !businessRevenueMetric &&
      !businessNetProfitMetric &&
      !businessNumberOfLeadsMetric &&
      !businessLeadsConversionRateMetric
    )
  }, [
    isAnyMetricLoading,
    businessRevenueMetric,
    businessNetProfitMetric,
    businessNumberOfLeadsMetric,
    businessLeadsConversionRateMetric,
  ])

  return {
    userBusinessGoals,
    isUserBusinessGoalsLoading,
    refetchUserBusinessGoals,
    completedUserBusinessGoals,
    completedTouchdowns: completedTouchdowns.current,
    goalStats,
    handleToggle,
    showInternalMeetingsOnly,
    periods: {
      totalRevenuePeriod,
      setTotalRevenuePeriod,
      netProfitPeriod,
      setNetProfitPeriod,
      numberOfLeadsPeriod,
      setNumberOfLeadsPeriod,
      leadConversionRatePeriod,
      setLeadConversionRatePeriod,
    },
    statsData,
    isRevenueLoading,
    isNetProfitLoading,
    isNumberOfLeadsLoading,
    isLeadsConversionRateLoading,
    isMetricsViewEnabled,
    handleMetricsShare,
    revenueMetricError,
    netProfitError,
    numberOfLeadsError,
    leadsConversionRateError,
    refetchRevenueMetrics,
    refetchNetProfitMetrics,
    refetchNumberOfLeadsMetrics,
    refetchLeadsConversionRateMetrics,
    refetchAllMetrics,
    isAnyMetricLoading,
    isInitialLoading,
    businessRevenueMetric,
    businessNetProfitMetric,
    businessNumberOfLeadsMetric,
    businessLeadsConversionRateMetric,
  }
}
